import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { ConnectionAddressObject } from "@origin-digital/address-lookup";
import { getOffers } from "../services/lpgOffers";
import { errorForm, logger } from "@origin-digital/reporting-client";
import { MINIMUM_LOADING_TIME } from "../consts";
import { ILPGOffer } from "../model/ILPGOffer";
import { ILPGOffersResponse } from "../model/ILPGOffersResponse";
import { withKasada } from "src/kasada/withKasada";

export interface IContext {
  offersData?: ILPGOffersResponse;
  selectedOffer?: ILPGOffer;
  lookupAddress?: ConnectionAddressObject;
  error?: Error;
  loading?: boolean;
  /**
   * True when the primary address call timed out and the fallback
   * (asyncMeterResolution=true) path was used. Downstream components should
   * suppress NG/MIRN-dependent messaging when this flag is set.
   */
  krakenOutage?: boolean;
}

export interface IAppContext {
  context: IContext;
  setContext(context: Partial<IContext> | IContext): void;
}

const defaultContext = {
  context: {
    loading: false,
  },
  setContext: /* istanbul ignore next */ () => undefined,
};

export const AppContext = createContext<IAppContext>(defaultContext);

export const mergeContext =
  ({ setContext, context }: IAppContext) =>
  (nextContext: Partial<IContext>): void =>
    setContext({
      ...context,
      ...nextContext,
    });

export const resolveAfterMinimumLoadTime = (
  startTime: number
): ((res: ILPGOffersResponse) => Promise<ILPGOffersResponse>) => {
  return (res: ILPGOffersResponse) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(res);
      }, MINIMUM_LOADING_TIME - (Date.now() - startTime));
    });
  };
};

export const rejectAfterMinimumLoadTime = (
  startTime: number
): ((error: any) => Promise<any>) => {
  return (error: any) => {
    return new Promise((_, reject) => {
      setTimeout(() => {
        reject(error);
      }, MINIMUM_LOADING_TIME - (Date.now() - startTime));
    });
  };
};

export const resolveOffersInContext =
  (context: IContext, setContext: Dispatch<SetStateAction<IContext>>) =>
  (res: ILPGOffersResponse) => {
    logger.debug("[AppContext.getLPGOffers] Fetch result", { res });
    setContext({ ...context, offersData: res, loading: false });
  };

const logErrorFetchingNationalOffers = (message: string, error: any) => {
  logger.error(message, {
    message: error.message,
    url: error?.fetchResponse?.config?.url,
    method: error?.fetchResponse?.config?.method,
    headers: error?.fetchResponse?.trackingHeaders,
    error,
  });
  errorForm({
    errorMessage: "Uh-oh, something went wrong. It’s not you, it’s us",
    errorCode: `${message} - ${error.message}`,
  });
};

export const rejectOffersInContext =
  (context: IContext, setContext: Dispatch<SetStateAction<IContext>>) =>
  (error: any) => {
    logErrorFetchingNationalOffers(
      "[AppContext.getLPGOffers] Fetch Error",
      error
    );
    setContext({ ...context, offersData: undefined, loading: false });
  };

export const getLPGOffersEffect = (
  context: IContext,
  setContext: Dispatch<SetStateAction<IContext>>
) => {
  return () => {
    const { lookupAddress, loading } = context;

    if (!loading) {
      return;
    }

    const query = new URLSearchParams(window?.location?.search);
    const offercode = query.get("offercode");
    logger.debug("[AppContext.getLPGOffers] Fetching lpg offers", {
      lookupAddress,
      offercode,
    });

    const startTime = Date.now();
    getOffers(lookupAddress, offercode === null ? undefined : offercode)
      .then(
        resolveAfterMinimumLoadTime(startTime),
        rejectAfterMinimumLoadTime(startTime)
      )
      .then(
        resolveOffersInContext(context, setContext),
        rejectOffersInContext(context, setContext)
      )
      .catch((error) => {
        logErrorFetchingNationalOffers(
          "[AppContext.getLPGOffers] Unhandled error",
          error
        );
      });
  };
};

export const useAppContext = (): IAppContext =>
  useContext<IAppContext>(AppContext);

export const AppContextProvider: React.FC = ({ children }) => {
  const [context, setContext] = useState<IContext>(defaultContext.context);
  useEffect(() => {
    withKasada(getLPGOffersEffect(context, setContext));
  }, [context.lookupAddress, context.loading]);

  return (
    <AppContext.Provider
      value={{
        context,
        setContext: mergeContext({ setContext, context }),
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

const AppContextConsumer = AppContext.Consumer;
export { AppContextConsumer };
